import { useEffect, useMemo, useState } from 'react';
import SafeIcon from './SafeIcon';
import type { IconName } from './SafeIcon';
import { useApp } from '../i18n/AppContext';
import { GOOGLE_FONTS_TOTAL, fontStack, googleFonts, loadFont } from '../data/googleFonts';
import type { CatalogueFont } from '../data/googleFonts';
import type { BrandFont } from '../store/seed';

const POLISH_PANGRAM = 'Pchnąć w tę łódź jeża lub ośm skrzyń fig';
const PREVIEWED_FONT_COUNT = 24;
const FONT_LOAD_TIMEOUT_MS = 2500;

const categories = ['All', 'Sans serif', 'Serif', 'Display', 'Handwriting', 'Monospace'] as const;
const roles: BrandFont['role'][] = ['Headings', 'Body text', 'Accent'];

type FontLoadState = 'loading' | 'ready' | 'fallback';

const fontLoadStatusClass: Record<FontLoadState, string> = {
  ready: 'published',
  loading: 'neutral',
  fallback: 'review',
};
const fontLoadIcon: Record<FontLoadState, IconName> = {
  ready: 'FiCheckCircle',
  loading: 'FiLoader',
  fallback: 'FiAlertTriangle',
};

function useFontReady(family: string | undefined) {
  const [ready, setReady] = useState<FontLoadState>('loading');
  useEffect(() => {
    if (!family) return;
    setReady('loading');
    let isStillWatching = true;
    const isFontFaceLoaded = () =>
      document.fonts.check(`16px "${family}"`) &&
      [...document.fonts].some(
        (face) => face.family.replace(/"/g, '') === family && face.status === 'loaded'
      );
    document.fonts
      .load(`16px "${family}"`)
      .then(() => {
        if (isStillWatching) setReady(isFontFaceLoaded() ? 'ready' : 'fallback');
      })
      .catch(() => isStillWatching && setReady('fallback'));
    const timeoutId = window.setTimeout(
      () => isStillWatching && setReady(isFontFaceLoaded() ? 'ready' : 'fallback'),
      FONT_LOAD_TIMEOUT_MS
    );
    return () => {
      isStillWatching = false;
      window.clearTimeout(timeoutId);
    };
  }, [family]);
  return ready;
}

export default function FontPicker({
  initialRole,
  replacing,
  onCancel,
  onChoose,
}: {
  initialRole: BrandFont['role'];
  replacing?: BrandFont;
  onCancel: () => void;
  onChoose: (font: BrandFont) => void;
}) {
  const { translate } = useApp();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<(typeof categories)[number]>('All');
  const [polishOnly, setPolishOnly] = useState(true);
  const [tab, setTab] = useState<'recommended' | 'all'>('recommended');
  const [preview, setPreview] = useState(POLISH_PANGRAM);
  const [selected, setSelected] = useState<CatalogueFont | undefined>(
    googleFonts.find((font) => font.family === replacing?.family)
  );
  const [role, setRole] = useState<BrandFont['role']>(replacing?.role ?? initialRole);
  const ready = useFontReady(selected?.family);
  const fontLoadLabel =
    ready === 'ready'
      ? translate('Loaded')
      : ready === 'loading'
        ? translate('Loading…')
        : translate('Could not load, showing a fallback');

  const visibleFonts = useMemo(
    () =>
      googleFonts.filter(
        (font) =>
          (tab === 'all' ||
            font.popular ||
            font.category === 'Serif' ||
            font.category === 'Sans serif') &&
          (category === 'All' || font.category === category) &&
          (!polishOnly || font.polish) &&
          font.family.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [query, category, polishOnly, tab]
  );

  useEffect(() => {
    visibleFonts.slice(0, PREVIEWED_FONT_COUNT).forEach((font) => loadFont(font.family, [400]));
  }, [visibleFonts]);
  useEffect(() => {
    if (selected) loadFont(selected.family, selected.weights);
  }, [selected]);

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        className="drawer font-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="font-picker-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="font-picker-title">{translate(replacing ? 'Change font' : 'Add a font')}</h2>
            <div className="muted">{translate('From the Google Fonts library')}</div>
          </div>
          <button className="icon-button" onClick={onCancel} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <div className="font-picker-tools">
          <input
            className="search-field"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={translate('Search font families')}
            aria-label={translate('Search font families')}
            autoFocus
          />
          <div className="segment brand-pill-group" role="group" aria-label={translate('Show')}>
            {(['recommended', 'all'] as const).map((item) => (
              <button
                key={item}
                className={tab === item ? 'selected' : ''}
                aria-pressed={tab === item}
                onClick={() => setTab(item)}
              >
                {translate(item === 'recommended' ? 'Recommended' : 'All fonts')}
              </button>
            ))}
          </div>
          <div className="font-filter-row">
            {categories.map((item) => (
              <button
                key={item}
                className={`channel-chip ${category === item ? 'selected' : ''}`}
                aria-pressed={category === item}
                onClick={() => setCategory(item)}
              >
                {translate(item)}
              </button>
            ))}
            <label className="brand-check compact">
              <input
                type="checkbox"
                checked={polishOnly}
                onChange={(event) => setPolishOnly(event.target.checked)}
              />{' '}
              {translate('Supports Polish letters')}
            </label>
          </div>
          <label className="font-preview-input">
            <span className="muted">{translate('Preview text')}</span>
            <input
              className="text-field"
              value={preview}
              onChange={(event) => setPreview(event.target.value)}
            />
          </label>
        </div>
        <p className="muted small-print">
          {translate(
            'Demo catalogue: {n} of {total}+ Google Fonts families. The live app searches the full library.',
            { n: googleFonts.length, total: GOOGLE_FONTS_TOTAL.toLocaleString() }
          )}
        </p>

        <div className="font-picker-body">
          <div className="font-results" role="listbox" aria-label={translate('Fonts')}>
            {visibleFonts.length === 0 && (
              <p className="muted">{translate('No fonts match. Try another name or category.')}</p>
            )}
            {visibleFonts.map((font) => (
              <button
                key={font.family}
                role="option"
                aria-selected={selected?.family === font.family}
                className={`font-result ${selected?.family === font.family ? 'selected' : ''}`}
                onClick={() => setSelected(font)}
              >
                <span className="font-result-name">
                  {font.family} <span className="muted">· {translate(font.category)}</span>
                  {!font.polish && <span className="tag">{translate('No Polish letters')}</span>}
                </span>
                <span
                  className="font-result-sample"
                  style={{ fontFamily: fontStack(font.family, font.category) }}
                >
                  {preview || 'The quick brown fox jumps over the lazy dog'}
                </span>
              </button>
            ))}
          </div>

          {selected && (
            <div className="font-detail">
              <div className="font-detail-head">
                <strong>{selected.family}</strong>
                <span className={`status ${fontLoadStatusClass[ready]}`}>
                  <SafeIcon name={fontLoadIcon[ready]} size={11} />
                  {fontLoadLabel}
                </span>
              </div>
              <p
                className="brand-pangram"
                style={{ fontFamily: fontStack(selected.family, selected.category) }}
              >
                The quick brown fox jumps over the lazy dog
              </p>
              <p
                className="brand-pangram-pl"
                style={{ fontFamily: fontStack(selected.family, selected.category) }}
              >
                {preview || POLISH_PANGRAM}
              </p>
              <div className="font-weights">
                {selected.weights.map((weight) => (
                  <span
                    key={weight}
                    style={{
                      fontFamily: fontStack(selected.family, selected.category),
                      fontWeight: weight,
                    }}
                  >
                    {weight} Aa Ąą
                  </span>
                ))}
              </div>
              <div
                className="font-caption-sample"
                style={{ fontFamily: fontStack(selected.family, selected.category) }}
              >
                <span className="font-caption-heading">{translate('Winter menu')}</span>
                <span>
                  {translate(
                    'Our gingerbread latte is back, made with beans we roast ourselves in Warsaw. Come in and take the window seat.'
                  )}
                </span>
              </div>
              <label htmlFor="font-role">
                <strong>{translate('Use this font for')}</strong>
              </label>
              <select
                id="font-role"
                className="select-field"
                value={role}
                onChange={(event) => setRole(event.target.value as BrandFont['role'])}
              >
                {roles.map((item) => (
                  <option key={item} value={item}>
                    {translate(item)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="button-row font-picker-actions">
          <button
            className="button primary"
            disabled={!selected}
            onClick={() =>
              selected && onChoose({ role, family: selected.family, category: selected.category })
            }
          >
            <SafeIcon name="FiCheck" />
            {translate(replacing ? 'Replace in draft' : 'Add to brand draft')}
          </button>
          <button className="button" onClick={onCancel}>
            {translate('Cancel')}
          </button>
        </div>
      </section>
    </div>
  );
}
